/* =========================================================================
   Shapes — the SHARED mock dataset every candidate layout renders.

   One dataset, five layouts: the only variable between subtabs is the geometry
   function, so the comparison is apples-to-apples. Modelled on the incoming
   plan's headline shape — 59 nodes spread across 11 tracks (the current shipped
   plan is 55 nodes across 4 tracks).

   This module is a self-contained mock. It deliberately does NOT import from
   ../DevelopmentPlan — the Shapes page must never be able to affect the real
   skill tree.
   ========================================================================= */

/** Synthetic id for the hub node every prereq-less node wires back to. */
export const CORE_ID = 'core';

export interface ShapeTrack {
  code: string;
  name: string;
  color: string;
  order: number;
}

export interface ShapeNode {
  code: string;
  track_code: string;
  name: string;
  tier: string;
  /** Layout depth = longest same-track prerequisite chain (precomputed here). */
  depth: number;
  prereqs: string[];
  /** Presentational only — populate the card's foot row like the shipped node
   *  (rank pips + game-count flask). Not used by any layout. */
  maxRank: number;
  games: number;
}

export interface ShapeEdge {
  from: string;
  to: string;
  soft: boolean;
  crossTrack: boolean;
}

/* ---- Card footprint -----------------------------------------------------
   The card matches the shipped `.devplan-node`: 176px content + 2x12px padding
   = 200px wide, and content-driven height (no fixed height — as tall as its
   content, exactly like the real node). CARD_H here is the worst-case bound
   used ONLY for the overlap metric + world sizing: a 2-line-title card is
   ~96px, so 100 is a safe over-estimate. The non-overlap PROOF needs only
   PITCH >= CARD_W, so CARD_H's exact value never weakens it.                 */
export const CARD_W = 200;
export const CARD_H = 100;

/* ---- Tracks -------------------------------------------------------------
   11 categorical hues, validated with the dataviz palette validator on the
   adjacent pairlist (light mode): lightness band, chroma floor, CVD separation
   (worst adjacent ΔE 8.3 protan) and normal-vision floor (worst 20.2) all pass.
   Three hues sit under 3:1 contrast on the light surface, so identity is never
   colour-alone here: every node carries a visible code badge, every track is a
   spatially separate ray/lane, and the legend names all 11.                  */
const TRACK_DEFS: Array<{
  code: string;
  name: string;
  color: string;
  prefix: string;
  /** nodes per depth layer, index = depth */
  layers: number[];
  skills: string[];
}> = [
  {
    code: 'trader',
    name: 'Trader',
    color: '#0f5fd6',
    prefix: 'TRD',
    layers: [2, 2, 2],
    skills: [
      'Position Expression',
      'Sizing Discipline',
      'Adding & Trimming',
      'Stop Placement',
      'Book Coherence',
      'Drawdown Behaviour',
    ],
  },
  {
    code: 'idea-generator',
    name: 'Idea Generator',
    color: '#eb6834',
    prefix: 'IDG',
    layers: [2, 2, 1],
    skills: [
      'Thesis Construction',
      'Catalyst Mapping',
      'Differentiation',
      'Cross-Asset Coherence',
      'Conviction Calibration',
    ],
  },
  {
    code: 'risk-manager',
    name: 'Risk Manager',
    color: '#0e9f6e',
    prefix: 'RSK',
    layers: [2, 2, 2],
    skills: [
      'Exposure Awareness',
      'Limit Discipline',
      'Correlation Risk',
      'Tail Scenarios',
      'De-risking Under Pain',
      'Risk Budget Allocation',
    ],
  },
  {
    code: 'quant',
    name: 'Quant',
    color: '#b845c4',
    prefix: 'QNT',
    layers: [2, 2, 1],
    skills: [
      'Carry & Roll',
      'Convexity Basics',
      'Model Scepticism',
      'Signal Construction',
      'Systematic Overlay',
    ],
  },
  {
    code: 'macro-research',
    name: 'Macro Research',
    color: '#eda100',
    prefix: 'MAC',
    layers: [1, 2, 2],
    skills: [
      'Regime Identification',
      'Policy Reaction Function',
      'Growth vs Inflation',
      'Cross-Country Rates',
      'Narrative vs Data',
    ],
  },
  {
    code: 'execution',
    name: 'Execution',
    color: '#0891a8',
    prefix: 'EXE',
    layers: [2, 2, 1],
    skills: [
      'Order Types',
      'Slippage Awareness',
      'Venue Selection',
      'Working Large Clips',
      'Execution Under Stress',
    ],
  },
  {
    code: 'portfolio-construction',
    name: 'Portfolio Construction',
    color: '#d14343',
    prefix: 'PFC',
    layers: [2, 2, 2],
    skills: [
      'Diversification',
      'Sizing Across Ideas',
      'Marginal Risk Contribution',
      'Hedge Selection',
      'Capital Efficiency',
      'Portfolio Rebalancing',
    ],
  },
  {
    code: 'behavioural',
    name: 'Behavioural Edge',
    color: '#7c5cff',
    prefix: 'BEH',
    layers: [2, 2, 1],
    skills: [
      'Loss Aversion',
      'Anchoring',
      'Overtrading',
      'Disposition Effect',
      'Process Under Pressure',
    ],
  },
  {
    code: 'cross-asset',
    name: 'Cross-Asset',
    color: '#00a3d9',
    prefix: 'XAS',
    layers: [1, 2, 1, 1],
    skills: [
      'FX & Rates Linkage',
      'Credit vs Equity',
      'Commodity Transmission',
      'Relative Value',
      'Basis & Spreads',
    ],
  },
  {
    code: 'liquidity',
    name: 'Liquidity & Financing',
    color: '#a06a00',
    prefix: 'LIQ',
    layers: [3, 2],
    skills: [
      'Funding Basics',
      'Repo & Collateral',
      'Margin Mechanics',
      'Liquidity Stress',
      'Balance-Sheet Cost',
    ],
  },
  {
    code: 'systematic',
    name: 'Systematic',
    color: '#e87ba4',
    prefix: 'SYS',
    layers: [2, 2, 2],
    skills: [
      'Backtest Hygiene',
      'Overfitting',
      'Signal Decay',
      'Rules vs Discretion',
      'Portfolio of Signals',
      'Live vs Sim Divergence',
    ],
  },
];

const TIER_BY_DEPTH = ['foundational', 'core', 'advanced', 'mastery'];

export const MOCK_TRACKS: ShapeTrack[] = TRACK_DEFS.map((t, i) => ({
  code: t.code,
  name: t.name,
  color: t.color,
  order: i,
}));

/**
 * Build the 59 nodes. Each layer's nodes take a prereq from the previous layer
 * (round-robin), so `depth` is exactly the same "longest same-track prereq
 * chain" the real engine computes — without needing the real engine.
 */
function buildNodes(): ShapeNode[] {
  const out: ShapeNode[] = [];
  TRACK_DEFS.forEach((t) => {
    let skillIdx = 0;
    let prevLayer: string[] = [];
    t.layers.forEach((count, depth) => {
      const thisLayer: string[] = [];
      for (let k = 0; k < count; k += 1) {
        const code = `${t.prefix}-${depth + 1}${String.fromCharCode(65 + k)}`;
        const name = t.skills[skillIdx] ?? `${t.name} skill ${skillIdx + 1}`;
        skillIdx += 1;
        out.push({
          code,
          track_code: t.code,
          name,
          tier: TIER_BY_DEPTH[Math.min(depth, TIER_BY_DEPTH.length - 1)],
          depth,
          prereqs: prevLayer.length ? [prevLayer[k % prevLayer.length]] : [],
          // Presentational variety for the card foot row (deterministic):
          // cycles games 1,2,0,1,2,0… so some cards show a flask and some don't.
          maxRank: 2,
          games: skillIdx % 3,
        });
        thisLayer.push(code);
      }
      prevLayer = thisLayer;
    });
  });
  return out;
}

export const MOCK_NODES: ShapeNode[] = buildNodes();

/** A few cross-track recommendations, so the long-link case is visible. */
const SOFT_LINKS: Array<[string, string]> = [
  ['RSK-2A', 'TRD-3A'],
  ['QNT-2A', 'SYS-2A'],
  ['MAC-2A', 'IDG-2A'],
  ['LIQ-1A', 'EXE-2A'],
  ['BEH-2A', 'PFC-3A'],
];

export function buildEdges(nodes: ShapeNode[]): ShapeEdge[] {
  const byCode: Record<string, ShapeNode> = {};
  nodes.forEach((n) => {
    byCode[n.code] = n;
  });

  const edges: ShapeEdge[] = [];
  nodes.forEach((n) => {
    if (n.prereqs.length === 0) {
      edges.push({ from: CORE_ID, to: n.code, soft: false, crossTrack: false });
    } else {
      n.prereqs.forEach((p) => {
        if (!byCode[p]) return;
        edges.push({
          from: p,
          to: n.code,
          soft: false,
          crossTrack: byCode[p].track_code !== n.track_code,
        });
      });
    }
  });

  SOFT_LINKS.forEach(([from, to]) => {
    if (!byCode[from] || !byCode[to]) return;
    edges.push({
      from,
      to,
      soft: true,
      crossTrack: byCode[from].track_code !== byCode[to].track_code,
    });
  });

  return edges;
}

export const MOCK_EDGES: ShapeEdge[] = buildEdges(MOCK_NODES);
